import { useApiBenefitConfigurationV10RunsCreate } from '@ben/api/gen';
import { getProgrammeList } from '@ben/columbia/api/get-programmes';
import type { EntityRef } from '@ben/columbia/api/requirements';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { apiErrorMessage, invalidateWorkspaceCaches } from './queries';

export type RunOperation = 'CREATE' | 'UPDATE';

interface StartRunArgs {
  groupId: string;
  employerId?: number;
  /** Programmes the linked benefit belongs to — the candidates for an update. */
  benefitProgrammes: EntityRef[];
}

/** A run waiting on the questions the confirmation dialog asks. */
export interface RunChoice {
  operation: RunOperation;
  programmes: EntityRef[];
}

/**
 * Resolving which programme a run belongs to.
 *
 * The API deliberately requires an explicit programme rather than guessing, so
 * the choice is made here: none is an error, exactly one is used without
 * asking, and several put the decision to the user.
 *
 * The candidates differ by operation. An update runs against the benefit the
 * group is already linked to, so its programmes come from that benefit. A build
 * has no benefit yet, so any of the employer's programmes will do.
 *
 * A build always confirms, even with a single candidate, because that is where
 * the auto-approve choice is made. The admin's own start-run form does the
 * same for the same reason.
 */
export const useStartRun = ({
  groupId,
  employerId,
  benefitProgrammes,
}: StartRunArgs) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<RunChoice | null>(null);

  const startRun = useApiBenefitConfigurationV10RunsCreate({
    mutation: {
      onSuccess: () => {
        setError(null);
        setChoice(null);
        invalidateWorkspaceCaches(queryClient);
      },
      onError: (mutationError: unknown) => {
        setError(apiErrorMessage(mutationError, 'Could not start the run.'));
        setChoice(null);
      },
    },
  });

  const candidatesFor = useCallback(
    async (operation: RunOperation): Promise<EntityRef[]> => {
      if (operation === 'UPDATE') return benefitProgrammes;
      if (employerId === undefined) return [];
      const programmes = await getProgrammeList(String(employerId));
      return programmes.map((programme) => ({
        id: programme.id,
        name: programme.name,
      }));
    },
    [benefitProgrammes, employerId]
  );

  const start = useCallback(
    async (operation: RunOperation) => {
      setError(null);
      let programmes: EntityRef[];
      try {
        programmes = await candidatesFor(operation);
      } catch (error_) {
        setError(apiErrorMessage(error_, 'Could not load programmes.'));
        return;
      }

      if (programmes.length === 0) {
        setError(
          operation === 'UPDATE'
            ? 'The linked benefit does not belong to any programme. Assign it to one before starting an update run.'
            : 'This employer has no programmes. Create one before starting a build run.'
        );
        return;
      }

      if (operation === 'UPDATE' && programmes.length === 1) {
        if (!startRun.isLoading) {
          startRun.mutate({
            data: {
              requirementGroup: groupId,
              operation,
              programme: programmes[0].id,
            },
          });
        }
        return;
      }

      setChoice({ operation, programmes });
    },
    [candidatesFor, startRun, groupId]
  );

  const confirmChoice = useCallback(
    (programmeId: number, autoApprove: boolean) => {
      if (!choice || startRun.isLoading) return;
      startRun.mutate({
        data: {
          requirementGroup: groupId,
          operation: choice.operation,
          programme: programmeId,
          // Only means anything for a build; the backend ignores it otherwise.
          autoApprove: choice.operation === 'CREATE' ? autoApprove : undefined,
        },
      });
    },
    [choice, startRun, groupId]
  );

  const cancelChoice = useCallback(() => setChoice(null), []);

  return {
    start,
    choice,
    confirmChoice,
    cancelChoice,
    error,
    isStarting: startRun.isLoading,
  };
};
